import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Lock, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "https://customer-assets.emergentagent.com/job_help-beacon-9/artifacts/ok1e76vb_17%20apr%202026%2C%2009_06_17.png";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const [email, setEmail] = useState('');
  const [nuovaPassword, setNuovaPassword] = useState('');
  const [confermaPassword, setConfermaPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const validate = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/password-reset/validate/${token}`);
        setEmail(data.email || '');
        setValid(true);
      } catch (e) {
        setError(e.response?.data?.detail || 'Link non valido o scaduto');
        setValid(false);
      } finally {
        setValidating(false);
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (nuovaPassword.length < 6) { setError('La password deve avere almeno 6 caratteri'); return; }
    if (nuovaPassword !== confermaPassword) { setError('Le password non coincidono'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/complete`, { token, nuova_password: nuovaPassword });
      toast.success('Password reimpostata con successo!');
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore durante il reset');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E8F4FD] pt-20 flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="border border-[#B8D4E8] bg-white p-8">
          <div className="flex items-center justify-center mb-6">
            <img src={LOGO} alt="LIFE LINKE" className="h-16 w-auto" />
          </div>

          <h1 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-[#0D1B2A] text-center mb-6">
            <Lock size={18} className="inline mr-2 text-[#FF3B30]" />
            Reimposta Password
          </h1>

          {validating ? (
            <div className="text-center py-8">
              <Loader2 size={32} className="animate-spin text-[#FF3B30] mx-auto mb-3" />
              <p className="text-sm text-[#4A6B8A]">Verifica del link in corso...</p>
            </div>
          ) : !valid ? (
            <div className="text-center space-y-4" data-testid="invalid-token">
              <div className="w-16 h-16 bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center justify-center mx-auto">
                <AlertTriangle size={32} className="text-[#FF3B30]" />
              </div>
              <p className="text-sm text-[#FF3B30] font-bold">{error}</p>
              <p className="text-xs text-[#4A6B8A]">Il link potrebbe essere scaduto o già utilizzato.</p>
              <div className="flex flex-col gap-2">
                <Link to="/login" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm hover:bg-[#FF1A1A] transition-all" data-testid="back-login-btn">
                  Torna al Login
                </Link>
                <Link to="/recupera-password" className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#B8D4E8] text-[#4A6B8A] font-bold uppercase tracking-widest text-sm hover:border-[#FF3B30] hover:text-[#0D1B2A] transition-all" data-testid="request-new-link-btn">
                  Richiedi nuovo link
                </Link>
              </div>
            </div>
          ) : success ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <div className="w-16 h-16 bg-[#32D74B]/10 border border-[#32D74B]/30 flex items-center justify-center mx-auto">
                <CheckCircle2 size={32} className="text-[#32D74B]" />
              </div>
              <p className="text-sm text-[#0D1B2A] font-bold">Password reimpostata con successo!</p>
              <p className="text-xs text-[#4A6B8A]">Puoi ora accedere con la nuova password</p>
              <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm hover:bg-[#FF1A1A] transition-all" data-testid="goto-login-btn">
                Vai al Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
              <p className="text-sm text-[#4A6B8A] text-center mb-2">
                Stai reimpostando la password per: <strong className="text-[#0D1B2A]">{email}</strong>
              </p>

              {error && (
                <div className="p-3 border border-[#FF3B30] bg-[#FF3B30]/10 flex items-center gap-2" data-testid="reset-error">
                  <AlertTriangle size={14} className="text-[#FF3B30]" />
                  <span className="text-xs text-[#FF3B30]">{error}</span>
                </div>
              )}

              <div>
                <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">Nuova Password</label>
                <input
                  type="password" value={nuovaPassword} onChange={(e) => setNuovaPassword(e.target.value)} required
                  className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">Conferma Password</label>
                <input
                  type="password" value={confermaPassword} onChange={(e) => setConfermaPassword(e.target.value)} required
                  className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                  data-testid="confirm-password-input"
                />
              </div>
              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#FF3B30] text-white font-bold uppercase tracking-widest hover:bg-[#FF1A1A] transition-all disabled:opacity-50 text-sm"
                data-testid="reset-submit-btn">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                Reimposta Password
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
